import strftime from 'strftime'
import Vue from 'vue'

let LOCALE = null
let TIME_FORMAT = null
const SHORT_MONTH_FORMAT = '%b'
const LONG_MONTH_FORMAT = '%B'

function _thisYear(date) {
  if (typeof date !== "object") return false
  const today = new Date();
  return date.getFullYear() === today.getFullYear()
}

function _isToday(date) {
  const today = new Date()
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
}

function _is24hours() {
  return TIME_FORMAT === "24h"
}

function _getDateFormats() {
  return LOCALE.date.formats
}

function _getTimeFormats() {
  return LOCALE.time.formats
}

function _getFormatter () {
  const dateFormats = LOCALE.date
  const timeFormats = LOCALE.time
  const locale = {
    ...timeFormats,
    days: dateFormats.day_names,
    shortDays: dateFormats.abbr_day_names,
    months: dateFormats.month_names.filter(mo => !!mo),
    shortMonths: dateFormats.abbr_month_names.filter(mo => !!mo),
  }

  return strftime.localize(locale)
}

const init = (locale, time_format) => {
  LOCALE = locale
  TIME_FORMAT = time_format
}

/**
* @name  time_today_or_date
* @param {String} dateIso - date as iso format
* @param {Object} options - short: true/false
*
* @return
* Today         - 10:00 pm
* This year     - Jul 8
* Not this year - Jul 8, 2012
*
* Note: am/pm depends on the business configuration
*/
const time_today_or_date = (dateIso, options = {}) => {
  const dateFormats = _getDateFormats()
  const timeFormats = _getTimeFormats()
  const formatter = _getFormatter()
  const date = new Date(dateIso)

  let format;
  if (_isToday(date)) {
    format = _is24hours() ? timeFormats.today_24h : timeFormats.today_12h
  } else if (_thisYear(date)) {
    format = dateFormats.month_without_year
  } else {
    format = options.short ? timeFormats.short : dateFormats.month_with_year
  }

  return formatter(format, date)
}

Vue.filter('time_today_or_date', time_today_or_date)

const date_range = (startIso, endIso, isFullMonth = false) => {
  const formatter = _getFormatter()
  const dateFormats = _getDateFormats()
  let startFormat = dateFormats.start_range
  let endFormat = dateFormats.end_range
  const start = new Date(startIso)
  const end = new Date(endIso)
  if (start.getMonth() !== end.getMonth()) {
    startFormat = dateFormats.start_range_with_month
    endFormat = dateFormats.long_end_range
  }
  if (start.getFullYear() !== end.getFullYear()) {
    startFormat = dateFormats.start_range_with_year
    endFormat = dateFormats.long_end_range
  }
  if (isFullMonth) {
    startFormat = startFormat.replace(SHORT_MONTH_FORMAT, LONG_MONTH_FORMAT)
    endFormat = endFormat.replace(SHORT_MONTH_FORMAT, LONG_MONTH_FORMAT)
  }
  return `${formatter(startFormat, start)} - ${formatter(endFormat, end)}`
}

/**
 * This is the main helper to show a date & time span of an event
 * Will show the full date (omits current year) and the end time (same day) or end date
 * @param startIso
 * @param endIso
 * @returns {string}
 *  # This year, same day       - Tue, July 8, 10:00am - 3:00pm
 *  # This year, different day  - Tue, July 8, 10:00am - Wed, July 9, 3:00pm
 *  # Not this year, same day   - Tue, July 8, 2014, 10:00am - 3:00pm
 *  # Different years           - Wed, December 31, 2014, 11:00pm - Thu, January 1, 2015, 8:00am
 */
const event_time = (startIso, endIso) => {
  const formatter = _getFormatter()
  const timeFormats = _getTimeFormats()
  let startFormat = _is24hours() ? timeFormats.day_without_year_short_month_24h : timeFormats.day_without_year_short_month_12h
  let endFormat = _is24hours() ? timeFormats.day_without_year_short_month_24h : timeFormats.day_without_year_short_month_12h
  const start = new Date(startIso)
  const end = new Date(endIso)

  if (start.getFullYear() !== end.getFullYear() || !_thisYear(start)) {
    startFormat = _is24hours() ? timeFormats.day_with_year_short_month_24h : timeFormats.day_with_year_short_month_12h
  }

  if (start.getDate() === end.getDate()) {
    endFormat = _is24hours() ? timeFormats.time_24h : timeFormats.time_12h
  } else if (start.getFullYear() !== end.getFullYear()) {
    endFormat = _is24hours() ? timeFormats.day_with_year_short_month_24h : timeFormats.day_with_year_short_month_12h
  }

  return `${formatter(startFormat, start)} - ${formatter(endFormat, end)}`
}

Vue.filter('event_time', event_time)

/**
* @name  date_no_current_year
* @param {String} dateIso - date as iso format
*
* @return
* example input: 2020-11-09T18:11:41.000+02:00
* This year     - Jul 8
* Not this year - Jul 8, 2012
*/
const date_no_current_year = (dateIso, isFullMonth = false, ignoreTZ = false, forceYear = false) => {
  const formatter = _getFormatter()
  const dateFormats = _getDateFormats()
  let date = new Date(dateIso)
  if (ignoreTZ) {
    // remove timezone offset
    date = new Date(date.getTime() + date.getTimezoneOffset() * 60000)
  }
  let format = !forceYear && _thisYear(date) ? dateFormats.month_without_year : dateFormats.month_with_year
  if (isFullMonth) {
    format = format.replace(SHORT_MONTH_FORMAT, LONG_MONTH_FORMAT);
  }
  return formatter(format, date)
}

Vue.filter('date_no_current_year', date_no_current_year)

/**
* @name  time_at
* @param {String} dateIso - date as iso format
*
* @return
* example input: 2020-11-09T18:11:41.000+02:00
* output: Mon, November 09 at 6:11pm
* Note: am/pm depends on the business configuration
*/
const time_at = (dateIso) => {
  const formatter = _getFormatter()
  const timeFormats = _getTimeFormats()
  const date = new Date(dateIso)
  let format;
  if (_thisYear(date)) {
    format = _is24hours() ? timeFormats.at_without_year_24h : timeFormats.at_without_year_12h
  } else {
    format = _is24hours() ? timeFormats.at_with_year_24h : timeFormats.at_with_year_12h
  }

  return formatter(format, date)
}

Vue.filter('time_at', time_at)

/**
 * @name  date_and_hour
 * @param {String} dateIso - date as iso format
 *
 * @return
 * example input: 2020-11-09T18:11:41.000+02:00
 * output this year: Nov 09, 6:11pm
 * output last year: Nov 09, 2020, 6:11pm
 * Note: am/pm depends on the business configuration
 */
const date_and_hour = (dateIso) => {
  const formatter = _getFormatter()
  const timeFormats = _getTimeFormats()
  const date = new Date(dateIso)
  let format;
  if (_thisYear(date)) {
    format = _is24hours() ? timeFormats.month_without_year_24h : timeFormats.month_without_year_12h
  } else {
    format = _is24hours() ? timeFormats.month_with_year_24h : timeFormats.month_with_year_12h
  }
  return formatter(format, date)
}
Vue.filter('date_and_hour', date_and_hour)

/**
* @name  time
* @param {String} dateIso - date as iso format
*
* @return
* example input: 2020-11-09T18:11:41.000+02:00
* output: 6:11 pm
* Note: am/pm depends on the business configuration
*/
const time = (dateIso) => {
  const formatter = _getFormatter()
  const timeFormats = _getTimeFormats()
  const format = _is24hours() ? timeFormats.today_24h :timeFormats.today_12h
  const date = new Date(dateIso)
  return formatter(format, date)
}
Vue.filter('time', time)

export default {
  init,
  time_today_or_date,
  date_no_current_year,
  time_at,
  time,
  date_and_hour,
  date_range,
  event_time
}
